import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Boxes } from 'lucide-react';
import { auth as authApi } from '@/api/endpoints';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@/components/ui/link';

const successBannerClass =
  'rounded-control bg-[var(--color-success-bg)] px-3 py-2 text-sm text-[var(--color-success)]';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  // The backend always resolves 202 regardless of whether the email is registered
  // (anti-account-enumeration, issue #43 acceptance criterion #13) -- a network/5xx
  // failure is the *only* other outcome, and even then this page shows the exact
  // same generic message rather than distinguishing "email not found" from
  // "something broke", which would itself leak enumeration information over time.
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authApi.forgotPassword({ email });
    } catch {
      // Intentionally swallowed: see the comment on `submitted` above.
    } finally {
      setBusy(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Boxes className="h-6 w-6" />
          </div>
          <CardTitle>{t('auth.forgotPasswordTitle')}</CardTitle>
          <CardDescription>{t('auth.forgotPasswordDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div role="status" aria-live="polite" className={successBannerClass}>
              {t('auth.forgotPasswordGenericMessage')}
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" aria-label="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <Button type="submit" className="w-full" loading={busy}>
                {t('auth.forgotPasswordSubmit')}
              </Button>
            </form>
          )}
          <p className="mt-4 text-center text-sm text-muted-foreground">
            <Link to="/login" intent="inline" className="font-medium">
              {t('auth.backToLogin')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
